import type { LDContext } from 'launchdarkly-react-client-sdk';
import { withLDProvider } from 'launchdarkly-react-client-sdk';

import { LAUNCH_DARKLY_CLIENT_SIDE_ID } from '#src/config.ts';
import { local } from '#src/lib/storage/index.ts';
import { localStorage } from '#src/lib/storage/localStorage.ts';

// Seconds to wait for flags before rendering with default values.
// Without it, a flag request that times out never settles (the SDK ignores
// the XHR `timeout` event) and the app stays blank.
const INIT_TIMEOUT = 5;

// Shared by all logged-out visitors so they count as a single LaunchDarkly
// user (pricing).
export const LOGGED_OUT_CONTEXT: LDContext = {
  kind: 'user',
  key: '00000000-0000-0000-0000-000000000000',
};

export const loadFlagsContext = (): LDContext => {
  const saved: unknown = localStorage.getItem('featureFlagsContext');
  return saved && typeof saved === 'object' && 'key' in saved
    ? (saved as LDContext)
    : LOGGED_OUT_CONTEXT;
};

export const saveFlagsContext = (context: LDContext) => {
  localStorage.setItem('featureFlagsContext', context);
};

export const forgetFlagsContext = () => {
  localStorage.removeItem('featureFlagsContext');
};

const FLAGS_CACHE_PREFIX = `ld:${LAUNCH_DARKLY_CLIENT_SIDE_ID}:`;

// Same key as the SDK: base64 of the UTF-8 JSON context
// (launchdarkly-js-sdk-common PersistentFlagStore).
const flagsCacheKey = (context: LDContext) => {
  const bytes = new TextEncoder().encode(JSON.stringify(context));
  return `${FLAGS_CACHE_PREFIX}${btoa(String.fromCharCode(...bytes))}`;
};

// The SDK caches flags per context and only clears the one it leaves on
// `identify`. The user context changes with every deploy (`webVersion`), so
// drop all but `keep`.
export const pruneFlagsCache = (keep: LDContext[]) => {
  const keepKeys = new Set(keep.map(flagsCacheKey));
  Array.from({ length: local.length }, (_, i) => local.key(i))
    .filter(
      (key): key is string =>
        !!key &&
        key.startsWith(FLAGS_CACHE_PREFIX) &&
        // SDK internals, like `$diagnostics`
        !key.startsWith(`${FLAGS_CACHE_PREFIX}$`) &&
        !keepKeys.has(key)
    )
    .forEach(key => local.removeItem(key));
};

export const withFFProvider = () => {
  const context = loadFlagsContext();
  // The logged-out cache only lasts until the next sign-in: `identify` clears
  // the cache of the context it leaves.
  pruneFlagsCache([LOGGED_OUT_CONTEXT, context]);

  return withLDProvider({
    clientSideID: LAUNCH_DARKLY_CLIENT_SIDE_ID,
    timeout: INIT_TIMEOUT,
    options: {
      // Serve the flags cached for `context` right away, then refresh them in
      // the background.
      bootstrap: 'localStorage',
      sendEventsOnlyForVariation: true,
      // Streams only once a signed-in user is known (see StreamWhenSignedIn).
      streaming: false,
      diagnosticOptOut: true,
    },
    context,
  });
};
